import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, ArrowRight, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function Sell() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", location: "", budget: "", message: "" });
  const [sub, setSub] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.location) {
      toast.error("Please provide name, phone and property location.");
      return;
    }
    setSub(true);
    try {
      await api.post("/leads", {
        ...form,
        service_type: "list_property",
        source_page: "sell",
      });
      toast.success("Thank you. Our team will reach out to list your property.");
      setForm({ name: "", phone: "", email: "", location: "", budget: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit.");
    } finally {
      setSub(false);
    }
  };

  const input = "w-full bg-white border border-[#E2E8F0] rounded-lg px-3.5 py-2.5 text-sm text-[#1F2937] focus:outline-none focus:border-[#183B66]";

  return (
    <>
      <section className="bg-[#101827] text-white">
        <div className="ae-container pt-28 pb-16">
          <div className="ae-eyebrow mb-3">Sell Your Property</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold max-w-3xl">
            List with Apurba Estate. Reach the right buyers.
          </h1>
          <p className="text-white/80 mt-5 max-w-3xl leading-relaxed">
            Share a few details about your property. We'll coordinate verification, positioning, listing and buyer enquiries.
          </p>
        </div>
      </section>

      <section className="ae-section">
        <div className="ae-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-4">
            <h3 className="font-display text-2xl font-semibold text-[#101827] mb-2">What happens next</h3>
            {[
              "We call back within one business day to understand your property.",
              "Site visit scheduled where applicable; initial document review.",
              "Honest positioning and asking-price discussion.",
              "Property listed and marketed to our buyer network.",
              "Qualified enquiries routed to you with full context.",
            ].map((t, i) => (
              <div key={i} className="flex items-start gap-3 text-[#1F2937]">
                <CheckCircle2 className="w-5 h-5 text-[#059669] shrink-0 mt-0.5" /> <span>{t}</span>
              </div>
            ))}
            <Link to="/services" className="ae-btn-outline mt-4">Learn about our services <ArrowRight className="w-4 h-4" /></Link>
          </div>

          <div className="lg:col-span-5">
            <form onSubmit={submit} className="ae-card p-6 space-y-3" data-testid="sell-form">
              <div className="ae-eyebrow mb-1">List your property</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input className={input} placeholder="Owner Name *" value={form.name} onChange={update("name")} data-testid="sell-name" />
                <input className={input} placeholder="Phone *" value={form.phone} onChange={update("phone")} data-testid="sell-phone" />
              </div>
              <input className={input} placeholder="Email" value={form.email} onChange={update("email")} />
              <input className={input} placeholder="Property Location *" value={form.location} onChange={update("location")} data-testid="sell-location" />
              <input className={input} placeholder="Expected Price (e.g. ₹ 50 L)" value={form.budget} onChange={update("budget")} />
              <textarea className={input + " min-h-[90px]"} placeholder="Describe your property (area, type, documents)" value={form.message} onChange={update("message")} />
              <button disabled={sub} className="ae-btn-primary w-full justify-center" data-testid="sell-submit">
                <Send className="w-4 h-4" /> {sub ? "Submitting…" : "Submit Listing Request"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
